import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import CommonPageHeader from '../components/common/CommonPageHeader';
import DateTimePicker from '../components/common/DateTimePicker';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon, ArrowRightOnRectangleIcon, ArrowLeftOnRectangleIcon, ClipboardDocumentListIcon, PlusIcon, PencilSquareIcon, TrashIcon,
  ChevronRightIcon, ArrowPathIcon, ArrowDownTrayIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

interface Change { field: string; label: string; from: string | null; to: string | null }
interface Entry {
  id: string;
  entity: 'task' | 'auth' | string;
  entityId?: string;
  entityLabel?: string;
  action: string;
  actorId?: string | null;
  actorName?: string | null;
  organizationId?: string | null;
  changes: Change[];
  summary?: string;
  meta?: { method?: string; userAgent?: string; screen?: string };
  at: Date;
}
interface Group { key: string; actorId?: string | null; actorName?: string | null; action: string; entity: string; fields: string; items: Entry[]; from: Date; to: Date }

type Period = 'today' | '7d' | '30d' | 'custom';
const dayStart = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const toDate = (v: any): Date => (v?.toDate ? v.toDate() : v instanceof Date ? v : new Date(v));
const fmtT = (d: Date) => d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
const fmtD = (d: Date) => d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

const ACTION: Record<string, { label: string; icon: React.ElementType; cls: string }> = {
  create: { label: 'létrehozta', icon: PlusIcon, cls: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300' },
  update: { label: 'módosította', icon: PencilSquareIcon, cls: 'text-sky-600 bg-sky-50 dark:bg-sky-900/30 dark:text-sky-300' },
  delete: { label: 'törölte', icon: TrashIcon, cls: 'text-rose-600 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-300' },
  login: { label: 'belépett', icon: ArrowRightOnRectangleIcon, cls: 'text-primary-700 bg-primary-50 dark:bg-primary-900/30 dark:text-primary-300' },
  logout: { label: 'kilépett', icon: ArrowLeftOnRectangleIcon, cls: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300' },
  login_failed: { label: 'sikertelen belépés', icon: ExclamationTriangleIcon, cls: 'text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300' }
};
const browserOf = (ua?: string) => { if (!ua) return ''; const m = ua.includes('Edg/') ? 'Edge' : ua.includes('Chrome/') ? 'Chrome' : ua.includes('Firefox/') ? 'Firefox' : ua.includes('Safari/') ? 'Safari' : 'böngésző'; const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : ''; return [m, os].filter(Boolean).join(' · '); };

/** Napló oldal: ki, mikor, mit csinált – belépések és feladat-változások. Admin: saját cég; super admin: minden cég. */
const AuditLog: React.FC = () => {
  const { user } = useAuth();
  const { users } = useUsers();
  const navigate = useNavigate();
  const isSuper = (user as any)?.role === 'super_admin';
  const myOrg = (user as any)?.organizationId as string | undefined;

  const [period, setPeriod] = useState<Period>('7d');
  const [custom, setCustom] = useState<{ from?: Date; to?: Date }>({});
  const [actor, setActor] = useState('');
  const [entity, setEntity] = useState<'all' | 'task' | 'project' | 'auth'>('all');
  const [field, setField] = useState('');
  const [text, setText] = useState('');
  const [orgScope, setOrgScope] = useState<'mine' | 'all'>('mine');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const range = useMemo(() => {
    const now = new Date();
    if (period === 'today') return { from: dayStart(now), to: now };
    if (period === '7d') { const f = dayStart(now); f.setDate(f.getDate() - 6); return { from: f, to: now }; }
    if (period === '30d') { const f = dayStart(now); f.setDate(f.getDate() - 29); return { from: f, to: now }; }
    const f = custom.from ? dayStart(custom.from) : dayStart(now); const t = custom.to ? new Date(custom.to) : now; t.setHours(23, 59, 59, 999);
    return { from: f, to: t };
  }, [period, custom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const base = collection(db, 'auditLog');
        const cons: any[] = [];
        if (!(isSuper && orgScope === 'all')) { if (!myOrg) { setEntries([]); setLoading(false); return; } cons.push(where('organizationId', '==', myOrg)); }
        cons.push(where('at', '>=', Timestamp.fromDate(range.from)), where('at', '<=', Timestamp.fromDate(range.to)), orderBy('at', 'desc'), limit(1500));
        const snap = await getDocs(query(base, ...cons));
        const list: Entry[] = snap.docs.map(d => { const x = d.data() as any; return { id: d.id, ...x, changes: x.changes || [], at: toDate(x.at) }; });
        if (!cancelled) setEntries(list);
      } catch (e: any) {
        console.error(e);
        if (!cancelled) toast.error(e?.code === 'failed-precondition' ? 'Az index még épül – próbáld pár perc múlva' : 'A napló betöltése nem sikerült');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [range, myOrg, isSuper, orgScope]);

  const actorOptions = useMemo(() => {
    const m = new Map<string, string>();
    entries.forEach(e => { if (e.actorId) m.set(e.actorId, e.actorName || users.find(u => u.id === e.actorId)?.displayName || e.actorId); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'hu'));
  }, [entries, users]);
  const fieldOptions = useMemo(() => { const s = new Map<string, string>(); entries.forEach(e => e.changes.forEach(c => s.set(c.field, c.label))); return Array.from(s.entries()); }, [entries]);

  const filtered = useMemo(() => entries.filter(e =>
    (!actor || e.actorId === actor || (actor === '_unknown' && !e.actorId)) &&
    (entity === 'all' || e.entity === entity) &&
    (!field || e.changes.some(c => c.field === field)) &&
    (!text.trim() || `${e.entityLabel || ''} ${e.actorName || ''} ${e.changes.map(c => `${c.label} ${c.from} ${c.to}`).join(' ')}`.toLowerCase().includes(text.trim().toLowerCase()))
  ), [entries, actor, entity, field, text]);

  // Egymást követő, azonos szereplő + művelet + mezők → egy csoport (pl. „47 feladat felelőse átírva”)
  const groups = useMemo(() => {
    const out: Group[] = [];
    for (const e of filtered) {
      const fields = e.changes.map(c => `${c.field}=${c.to ?? ''}`).sort().join('|');
      const g = out[out.length - 1];
      if (g && g.actorId === e.actorId && g.action === e.action && g.entity === e.entity && g.fields === fields && e.entity !== 'auth' && g.to.getTime() - e.at.getTime() < 3 * 60000) { g.items.push(e); g.from = e.at; }
      else out.push({ key: e.id, actorId: e.actorId, actorName: e.actorName, action: e.action, entity: e.entity, fields, items: [e], from: e.at, to: e.at });
    }
    return out;
  }, [filtered]);

  const actorLabel = (e: { actorId?: string | null; actorName?: string | null }) => e.actorId === 'system' ? 'Rendszer' : e.actorName || users.find(u => u.id === e.actorId)?.displayName || (e.actorId ? e.actorId.slice(0, 8) + '…' : 'ismeretlen felhasználó');

  const exportCsv = () => {
    const rows = [['Időpont', 'Felhasználó', 'Művelet', 'Típus', 'Elem', 'Mező', 'Előtte', 'Utána']];
    for (const e of filtered) {
      if (e.changes.length === 0) rows.push([`${fmtD(e.at)} ${fmtT(e.at)}`, actorLabel(e), ACTION[e.action]?.label || e.action, e.entity, e.entityLabel || '', '', '', e.meta ? browserOf(e.meta.userAgent) : '']);
      for (const c of e.changes) rows.push([`${fmtD(e.at)} ${fmtT(e.at)}`, actorLabel(e), ACTION[e.action]?.label || e.action, e.entity, e.entityLabel || '', c.label, c.from || '', c.to || '']);
    }
    const csv = '﻿' + rows.map(r => r.map(v => /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = `naplo_${fmtD(range.from)}-${fmtD(range.to)}.csv`.replace(/\./g, ''); a.click();
  };

  const entityUrl = (e: Entry) => e.entity === 'project' ? `/projects/${e.entityId}` : `/tasks?task=${e.entityId}`;
  const toggle = (k: string) => setOpen(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // Nap szerinti szakaszok
  const days: { day: Date; groups: Group[] }[] = [];
  for (const g of groups) { const d = days[days.length - 1]; if (d && sameDay(d.day, g.to)) d.groups.push(g); else days.push({ day: g.to, groups: [g] }); }

  const stats = useMemo(() => ({
    logins: filtered.filter(e => e.action === 'login').length,
    changes: filtered.filter(e => e.entity !== 'auth' && e.action === 'update').length,
    created: filtered.filter(e => e.entity !== 'auth' && e.action === 'create').length,
    deleted: filtered.filter(e => e.entity !== 'auth' && e.action === 'delete').length,
    people: new Set(filtered.map(e => e.actorId).filter(Boolean)).size
  }), [filtered]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader title="Napló" subtitle="Ki, mikor, mit csinált – belépések és feladat-változások" icon={ShieldCheckIcon}>
        <button type="button" onClick={exportCsv} className="crm-btn crm-btn-secondary"><ArrowDownTrayIcon className="w-4 h-4" />CSV</button>
      </CommonPageHeader>

      <div className="p-3 sm:p-4 md:p-5 space-y-3">
        {/* Szűrők */}
        <div className="crm-card p-2.5 sm:p-3 flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="flex items-center gap-1 overflow-x-auto">
            {([['today', 'Ma'], ['7d', '7 nap'], ['30d', '30 nap'], ['custom', 'Egyedi']] as [Period, string][]).map(([id, l]) => (
              <button key={id} type="button" onClick={() => setPeriod(id)} className={`h-8 px-2.5 rounded-md text-xs font-medium whitespace-nowrap ${period === id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{l}</button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <DateTimePicker showTime={false} value={custom.from} onChange={d => setCustom(c => ({ ...c, from: d }))} placeholder="Ettől" className="w-36" />
              <span className="text-gray-400">–</span>
              <DateTimePicker showTime={false} value={custom.to} onChange={d => setCustom(c => ({ ...c, to: d }))} placeholder="Eddig" className="w-36" />
            </div>
          )}
          <select value={actor} onChange={e => setActor(e.target.value)} className="crm-input-compact border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 max-w-[200px]">
            <option value="">Minden felhasználó</option>
            {actorOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            <option value="_unknown">ismeretlen (régi kliens)</option>
          </select>
          <select value={entity} onChange={e => setEntity(e.target.value as any)} className="crm-input-compact border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
            <option value="all">Minden esemény</option>
            <option value="auth">Belépések</option>
            <option value="task">Feladatok</option>
            <option value="project">Projektek</option>
          </select>
          {entity !== 'auth' && (
            <select value={field} onChange={e => setField(e.target.value)} className="crm-input-compact border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
              <option value="">Bármely mező</option>
              {fieldOptions.map(([f, l]) => <option key={f} value={f}>{l}</option>)}
            </select>
          )}
          <input type="search" value={text} onChange={e => setText(e.target.value)} placeholder="Keresés (feladat, név, érték)…" className="crm-input-compact border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 w-full lg:w-56" />
          {isSuper && (
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none lg:ml-auto whitespace-nowrap">
              <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600" checked={orgScope === 'all'} onChange={e => setOrgScope(e.target.checked ? 'all' : 'mine')} />Minden cég
            </label>
          )}
        </div>

        {/* Összegzés */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[['Belépés', stats.logins], ['Módosítás', stats.changes], ['Létrehozás', stats.created], ['Törlés', stats.deleted], ['Felhasználó', stats.people]].map(([l, v]) => (
            <div key={String(l)} className="crm-card px-3 py-2"><div className="text-[11px] text-gray-500 dark:text-gray-400">{l}</div><div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{v}</div></div>
          ))}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400 inline-flex w-full justify-center items-center gap-2"><ArrowPathIcon className="w-4 h-4 animate-spin" />Betöltés…</div>
        ) : days.length === 0 ? (
          <div className="crm-card py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            Nincs bejegyzés az időszakban.<div className="text-xs mt-1">A napló a bekapcsolás pillanatától gyűjt – korábbi módosításokról nincs adat.</div>
          </div>
        ) : days.map(d => (
          <div key={d.day.toDateString()} className="crm-card overflow-hidden">
            <div className="px-3 py-1.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{fmtD(d.day)} · {d.groups.reduce((s, g) => s + g.items.length, 0)} esemény</div>
            <div className="divide-y divide-[var(--crm-border)]">
              {d.groups.map(g => {
                const A = ACTION[g.action] || ACTION.update; const Icon = A.icon;
                const multi = g.items.length > 1; const isOpen = open.has(g.key);
                const first = g.items[0];
                return (
                  <div key={g.key}>
                    <div className={`flex items-start gap-2.5 px-3 py-2 ${multi ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''}`} onClick={() => multi && toggle(g.key)}>
                      <span className="text-[11px] tabular-nums text-gray-400 w-10 pt-0.5 flex-shrink-0">{fmtT(g.to)}</span>
                      <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center flex-shrink-0 ${A.cls}`}><Icon className="w-3.5 h-3.5" /></span>
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{actorLabel(g)}</span>
                          {!g.actorId && <span className="text-[10px] px-1 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" title="A módosítás nem tartalmazott felhasználói pecsétet (régi kliens vagy külső írás)">ismeretlen</span>}
                          <span className="text-gray-600 dark:text-gray-300">{A.label}</span>
                          {g.entity !== 'auth' && (multi
                            ? <span className="text-gray-900 dark:text-gray-100 font-medium">{g.items.length} {g.entity === 'project' ? 'projektet' : 'feladatot'}</span>
                            : <><span className="text-[10px] uppercase tracking-wide text-gray-400">{g.entity === 'project' ? 'projekt' : 'feladat'}</span><button type="button" onClick={e => { e.stopPropagation(); if (first.entityId && g.action !== 'delete') navigate(entityUrl(first)); }} className="text-gray-900 dark:text-gray-100 hover:text-primary-600 hover:underline truncate max-w-[420px] text-left">{first.entityLabel || first.entityId}</button></>)}
                          {g.entity === 'auth' && first.meta && <span className="text-xs text-gray-400">{browserOf(first.meta.userAgent)}{first.meta.method ? ` · ${first.meta.method}` : ''}</span>}
                          {multi && <span className="text-xs text-gray-400">{fmtT(g.from)}–{fmtT(g.to)}</span>}
                          {multi && <ChevronRightIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />}
                        </div>
                        {first.changes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {first.changes.map(c => (
                              <span key={c.field} className="inline-flex items-center gap-1 text-[11px] px-1.5 h-5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 max-w-full">
                                <span className="text-gray-500 dark:text-gray-400">{c.label}:</span>
                                {c.from != null && c.from !== '' && g.action !== 'create' && <><span className="line-through opacity-60 truncate max-w-[160px]">{c.from}</span><span className="text-gray-400">→</span></>}
                                <span className="font-medium truncate max-w-[200px]">{c.to}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {multi && isOpen && (
                      <div className="pl-[68px] pr-3 pb-2 space-y-0.5">
                        {g.items.map(e => (
                          <div key={e.id} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <span className="tabular-nums text-gray-400 w-10">{fmtT(e.at)}</span>
                            <button type="button" onClick={() => e.entityId && g.action !== 'delete' && navigate(entityUrl(e))} className="truncate hover:text-primary-600 hover:underline text-left max-w-[520px]">{e.entityLabel || e.entityId}</button>
                            {e.changes.filter(c => c.from).slice(0, 2).map(c => <span key={c.field} className="text-gray-400 truncate">{c.label}: <span className="line-through">{c.from}</span> → {c.to}</span>)}
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-300" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {entries.length >= 1500 && <div className="text-xs text-center text-gray-500">Az első 1500 bejegyzés látszik – szűkítsd az időszakot.</div>}
      </div>
    </div>
  );
};

export default AuditLog;
